/**
 * silwy-remote-cart.js
 * =====================
 * Synchronisiert den Shopify-Warenkorb mit dem SILWY/Keystone Remote-Cart-API.
 *
 * Variante: PER-USER UserToken (theme-verwaltet via localStorage)
 *   Shopify's App Proxy reicht `Set-Cookie` nicht zuverlässig durch, deshalb
 *   verwaltet das Theme den Token selbst: Er wird in localStorage gehalten und
 *   bei jedem Cart-Call im Body (`UserToken`) bzw. an der Checkout-URL (`?ut=`)
 *   mitgeschickt. Der Proxy generiert beim ersten Call (kein Token vorhanden)
 *   einen frischen Token und gibt ihn in der Response zurück; das Theme
 *   speichert ihn. Jeder Besucher hat so seinen eigenen Warenkorb.
 *   Der Token identifiziert nur einen Cart (wie eine Session-ID) — der APIKey
 *   bleibt serverseitig im Proxy.
 *
 * Funktionsweise:
 *   - Hört auf das Shopify-Event `cart:change` (Hinzufügen / Entfernen / Mengenwechsel).
 *   - Berechnet das Delta gegenüber dem zuletzt bekannten Zustand.
 *   - Ruft `/apps/remote-cart/addtocart` bzw. `/removefromcart` über den App
 *     Proxy auf — ohne UserToken im Body.
 *   - Der Checkout-Link zeigt fest auf `/apps/remote-cart/checkout`; der Worker
 *     antwortet dort mit einem 302 zur Keystone-Checkout-URL.
 *   - Bei jedem Seitenaufruf (einmalig, direkt nach der Baseline-Initialisierung):
 *     ruft `GetCartSummary` ab und vergleicht die Mengen pro SKU mit dem lokalen
 *     Shopify-Warenkorb. Weicht die Menge ab (remote weniger ODER mehr als
 *     lokal — z.B. Änderung über einen anderen Kanal bei Keystone), wird der
 *     bestehende Shopify-Line-Item per `/cart/change.js` nachgezogen — inkl.
 *     Update des Cart-Icon-Counters und anderer `cart:change`-Listener
 *     (Mini-Cart etc.). SKUs, die remote existieren, lokal aber noch GAR NICHT
 *     im Cart liegen, werden NICHT neu angelegt — siehe `reconcileWithRemoteCart()`.
 *
 * API-Dokumentation: https://documenter.getpostman.com/view/5472417/2s93Xzw2X5
 */

(function () {
  'use strict';

  // TEMPORÄR (Ringo, 2026-09-17): Debug-Logging zur Fehlersuche "Button bleibt
  // verschwunden". Läuft IMMER (nicht hinter CONFIG.debug), Prefix "[SilwyGate]"
  // zum Filtern in der Konsole. Build-Marker hier oben zeigt sofort, ob der
  // Browser wirklich diese Version geladen hat (Cache-Verdacht). Nach Klärung
  // wieder entfernen bzw. hinter CONFIG.debug verschieben.
  console.log('[SilwyGate] silwy-remote-cart.js geladen - Debug-Build 2026-09-17-1');

  // ─── Konfiguration ────────────────────────────────────────────────────────────

  const CONFIG = {
    /**
     * Basis-URL für API-Calls — geht durch den Shopify App Proxy:
     *   Browser → silwy.de/apps/remote-cart/* → Proxy → silwymagnet-store.com
     * APIKey und UserToken werden vom Proxy serverseitig gesetzt.
     */
    apiBaseUrl: '/apps/remote-cart',

    /**
     * Keystone-ProductCode = productCodePrefix + Shopify-SKU.
     * z.B. SKU "S030-1313-1" → ProductCode "SWYS030-1313-1".
     * Intern rechnen wir mit der rohen SKU; nur der ausgehende ProductCode
     * bekommt das Präfix.
     */
    productCodePrefix: 'SWY',

    /**
     * Endpoint-Pfade (relativ zu apiBaseUrl).
     */
    endpoints: {
      addToCart:         '/addtocart',
      removeFromCart:    '/removefromcart',
      getCartSummary:    '/getcartsummary',
      checkAvailability: '/checkproductavailability',
      checkout:          '/checkout',
    },

    /** Maximale Anzahl Wiederholungsversuche bei fehlgeschlagenen API-Aufrufen */
    maxRetries: 2,

    /** Wartezeit (ms) zwischen Wiederholungsversuchen */
    retryDelay: 800,

    /** Verfügbarkeits-Cache: Gültigkeitsdauer pro SKU (ms) */
    availabilityTtlMs: 2 * 60 * 1000,

    /**
     * Timeout (ms) für den einzelnen Aufrufer einer Verfügbarkeitsprüfung
     * (Ringo, 2026-09-17: ATC-Button blieb auf einem Mobilgerät dauerhaft
     * unsichtbar - fetchWithRetry() hat kein eigenes Timeout, ein
     * hängender Request (z.B. schlechtes Mobilfunknetz, iOS/Safari
     * Netzwerk-Eigenheiten) lässt getAvailability() dann nie auflösen und
     * das PDP-Gate bleibt für immer `hidden`, weil revealKeystoneGate()
     * erst NACH dem await läuft. blockOnAvailabilityError deckt nur den
     * Fall ab, dass der Request tatsächlich fehlschlägt - nicht den Fall,
     * dass er einfach nie antwortet. Nach diesem Timeout wird pro Aufrufer
     * fail-open aufgelöst (siehe blockOnAvailabilityError); der eigentliche
     * Request läuft im Hintergrund weiter und füllt den Cache trotzdem,
     * falls er doch noch durchkommt.
     */
    availabilityTimeoutMs: 8000,

    /**
     * Verhalten, wenn die Verfügbarkeitsprüfung selbst fehlschlägt (Keystone
     * nicht erreichbar o.ä.):
     *   false → fail-open: Produkt NICHT sperren (Shop bleibt nutzbar; die harte
     *           Add-Sperre fängt echten Fehlbestand beim addtocart trotzdem ab).
     *   true  → fail-closed: bei Prüf-Fehler als ausverkauft behandeln.
     */
    blockOnAvailabilityError: false,

    /** Label für ausverkaufte Produkte */
    soldOutLabel: 'Ausverkauft',

    /** Debug-Logging aktivieren */
    debug: false,
  };

  // ─── Interner Zustand ─────────────────────────────────────────────────────────

  /**
   * Abbild des zuletzt bekannten Shopify-Warenkorbs als Map: { sku → quantity }
   */
  let previousCartState = {};

  /** Ob die Bibliothek erfolgreich initialisiert wurde */
  let initialized = false;

  /**
   * Promise-Kette für sequenzielle Operationen.
   * Startet mit init() — alle cart:change-Events reihen sich dahinter ein und
   * warten automatisch, bis die Baseline geladen ist.
   */
  let syncQueue;

  // ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

  function log(...args) {
    if (CONFIG.debug) console.log('[SilwyRemoteCart]', ...args);
  }
  function warn(...args)  { console.warn('[SilwyRemoteCart]', ...args); }
  function error(...args) { console.error('[SilwyRemoteCart]', ...args); }

  // ─── Token-Speicher (localStorage) ──────────────────────────────────────────
  // Der UserToken identifiziert den Warenkorb dieses Besuchers bei Keystone.
  // Er wird vom Proxy generiert, hier persistiert und bei jedem Call mitgeschickt.

  const TOKEN_STORAGE_KEY = 'silwy_user_token';

  function getStoredToken() {
    try {
      const t = window.localStorage.getItem(TOKEN_STORAGE_KEY);
      return t && t.trim() ? t.trim() : null;
    } catch (err) {
      warn('localStorage nicht verfügbar – Token kann nicht gelesen werden.', err);
      return null;
    }
  }

  function storeToken(token) {
    if (!token || typeof token !== 'string') return;
    try {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch (err) {
      warn('localStorage nicht verfügbar – Token kann nicht gespeichert werden.', err);
    }
  }

  /**
   * Konvertiert Shopify-Cart-Items zu einer Map: { sku → quantity }.
   * Items ohne SKU werden übersprungen.
   */
  function buildCartState(items) {
    const state = {};
    if (!Array.isArray(items)) return state;

    for (const item of items) {
      const sku = item.sku;
      if (!sku) {
        warn(`Item "${item.title}" hat keine SKU – wird nicht mit Remote-Cart synchronisiert.`);
        continue;
      }
      state[sku] = (state[sku] || 0) + item.quantity;
    }
    return state;
  }

  /**
   * Fetch mit automatischen Wiederholungsversuchen.
   */
  async function fetchWithRetry(url, options, retriesLeft = CONFIG.maxRetries) {
    try {
      const response = await fetch(url, options);
      if (!response.ok && retriesLeft > 0) {
        warn(`HTTP ${response.status} für ${url} – Wiederholung in ${CONFIG.retryDelay}ms (${retriesLeft} verbleibend)`);
        await new Promise(r => setTimeout(r, CONFIG.retryDelay));
        return fetchWithRetry(url, options, retriesLeft - 1);
      }
      return response;
    } catch (err) {
      if (retriesLeft > 0) {
        warn(`Netzwerkfehler für ${url} – Wiederholung in ${CONFIG.retryDelay}ms (${retriesLeft} verbleibend)`, err);
        await new Promise(r => setTimeout(r, CONFIG.retryDelay));
        return fetchWithRetry(url, options, retriesLeft - 1);
      }
      throw err;
    }
  }

  /**
   * POST an einen Proxy-Endpoint.
   */
  async function apiPost(endpoint, body) {
    const url = CONFIG.apiBaseUrl + endpoint;

    // Gespeicherten Token mitschicken (falls vorhanden). Der Proxy nutzt ihn;
    // fehlt er, generiert der Proxy einen neuen und gibt ihn zurück.
    const storedToken = getStoredToken();
    const payload = storedToken ? { ...body, UserToken: storedToken } : { ...body };

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    };
    log('POST', url, payload);
    const response = await fetchWithRetry(url, options);
    const data = await response.json();
    log('←', url, data);

    // Token aus der Response übernehmen (erster Call generiert ihn, spätere
    // Calls bestätigen ihn) und persistieren.
    if (data && typeof data.UserToken === 'string' && data.UserToken.trim()) {
      storeToken(data.UserToken.trim());
    }
    return data;
  }

  // ─── Remote-API-Methoden ──────────────────────────────────────────────────────
  // Hinweis: apiPost hängt den UserToken (aus localStorage) automatisch an.
  // Die Methoden nehmen die rohe Shopify-SKU entgegen und wandeln sie in den
  // Keystone-ProductCode (SWY + SKU) um.

  /**
   * Wandelt eine Shopify-SKU in den Keystone-ProductCode um.
   * z.B. "S030-1313-1" → "SWYS030-1313-1".
   */
  function toProductCode(sku) {
    return CONFIG.productCodePrefix + sku;
  }

  /**
   * Fügt ein Produkt zum Remote-Warenkorb hinzu.
   * API: POST /remoteapi/addtocart
   * @param {string} sku  Shopify-SKU (ohne Präfix)
   */
  async function addToCart(sku, quantity) {
    return apiPost(CONFIG.endpoints.addToCart, {
      ProductCode: toProductCode(sku),
      ProductQuantity: quantity,
    });
  }

  /**
   * Entfernt eine bestimmte Menge eines Produkts aus dem Remote-Warenkorb.
   * API: POST /remoteapi/RemoveFromCart
   * @param {string} sku  Shopify-SKU (ohne Präfix)
   */
  async function removeFromCart(sku, quantity) {
    return apiPost(CONFIG.endpoints.removeFromCart, {
      ProductCode: toProductCode(sku),
      ProductQuantityToRemove: quantity,
    });
  }

  /**
   * Ruft eine Zusammenfassung des Remote-Warenkorbs ab.
   * API: POST /remoteapi/GetCartSummary
   */
  async function getCartSummary(summaryType = 'full') {
    return apiPost(CONFIG.endpoints.getCartSummary, {
      SummaryType: summaryType,
    });
  }

  /**
   * Prüft die Verfügbarkeit eines Produkts im Remote-System.
   * API: POST /remoteapi/CheckProductAvailability (kein Token nötig)
   * Antwort-Shape: { QuantityAvailable, IsAvailable, CanBeSpecialOrdered, Successful }
   * @param {string} sku  Shopify-SKU (ohne Präfix)
   */
  async function checkProductAvailability(sku) {
    return apiPost(CONFIG.endpoints.checkAvailability, { ProductCode: toProductCode(sku) });
  }

  // ─── Verfügbarkeit: Live-Check mit Cache ──────────────────────────────────────

  const availabilityCache = new Map(); // sku → { available, quantity, ts }
  const availabilityInflight = new Map(); // sku → Promise (Dedup paralleler Calls)

  /**
   * Liefert die (gecachte) Keystone-Verfügbarkeit einer SKU.
   * @returns {Promise<{available:boolean, quantity:number, errored?:boolean}>}
   */
  /** Startet (oder liefert einen bereits laufenden) echten Verfügbarkeits-Check. */
  function startAvailabilityCheck(sku) {
    if (availabilityInflight.has(sku)) return availabilityInflight.get(sku);

    console.log(`[SilwyGate] Verfügbarkeits-Request gestartet für SKU "${sku}"`);
    const promise = (async () => {
      try {
        const r = await checkProductAvailability(sku);
        const result = {
          available: !!(r && r.Successful && r.IsAvailable),
          quantity: r && typeof r.QuantityAvailable === 'number' ? r.QuantityAvailable : 0,
          ts: Date.now(),
        };
        availabilityCache.set(sku, result); // nur erfolgreiche Ergebnisse cachen
        log(`Verfügbarkeit ${sku}:`, result.available ? `${result.quantity} verfügbar` : 'nicht verfügbar');
        console.log(`[SilwyGate] Antwort erhalten für SKU "${sku}":`, result, 'Rohdaten:', r);
        return result;
      } catch (err) {
        warn(`Verfügbarkeitsprüfung fehlgeschlagen für ${sku} – fail-${CONFIG.blockOnAvailabilityError ? 'closed' : 'open'}`, err);
        console.log(`[SilwyGate] Request fehlgeschlagen für SKU "${sku}" - fail-${CONFIG.blockOnAvailabilityError ? 'closed' : 'open'}`, err);
        // Fehler NICHT cachen → nächster Versuch prüft erneut.
        return { available: !CONFIG.blockOnAvailabilityError, quantity: 0, errored: true };
      } finally {
        availabilityInflight.delete(sku);
      }
    })();

    availabilityInflight.set(sku, promise);
    return promise;
  }

  async function getAvailability(sku) {
    if (!sku) return { available: false, quantity: 0 };

    const cached = availabilityCache.get(sku);
    if (cached && Date.now() - cached.ts < CONFIG.availabilityTtlMs) return cached;

    const pending = startAvailabilityCheck(sku);
    if (!CONFIG.availabilityTimeoutMs) return pending;

    // Pro Aufrufer gegen ein Timeout racen (nicht den Request selbst abbrechen -
    // der läuft im Hintergrund weiter, siehe startAvailabilityCheck/Kommentar
    // bei CONFIG.availabilityTimeoutMs).
    const timeout = new Promise((resolve) => {
      setTimeout(() => {
        warn(`Verfügbarkeitsprüfung für ${sku} dauert länger als ${CONFIG.availabilityTimeoutMs}ms – fail-${CONFIG.blockOnAvailabilityError ? 'closed' : 'open'} (Anfrage läuft im Hintergrund weiter).`);
        resolve({ available: !CONFIG.blockOnAvailabilityError, quantity: 0, errored: true, timedOut: true });
      }, CONFIG.availabilityTimeoutMs);
    });

    return Promise.race([pending, timeout]);
  }

  /** Öffentliche, gecachte Ja/Nein-Verfügbarkeit. */
  async function isAvailable(sku) {
    return (await getAvailability(sku)).available;
  }

  /**
   * Liest den aktuellen UpPromote-Linker-Wert, falls das UpPromote-Tracking-
   * Skript geladen ist (App-Embed injiziert `window.upTag` global). Fehlt es
   * (Skript noch nicht geladen, kein aktiver Affiliate-Klick, App deinstalliert
   * o.ä.), wird `null` zurückgegeben und der Checkout läuft unverändert weiter
   * (fail-open — Tracking ist nice-to-have, darf den Checkout nie blockieren).
   */
  function getUpPromoteLinkerValue() {
    try {
      if (typeof window.upTag !== 'function') return null;
      const value = window.upTag('app', 'linker');
      return value || null;
    } catch (err) {
      warn('UpPromote-Linker-Wert konnte nicht gelesen werden.', err);
      return null;
    }
  }

  /**
   * Gibt die (Proxy-)Checkout-URL zurück. Der Browser folgt dem 302 dort zur
   * tatsächlichen Keystone-Checkout-Seite (silwymagnet-store.com).
   *
   * UpPromote Linker-Tracking (Method 2 – manuelle Cross-Domain-Navigation,
   * https://docs.uppromote.com/settings/integrations/others#linker-tracking):
   * Der Checkout-Button ist kein normaler `<a href>`-Link, sondern wird per JS
   * ausgelöst und springt über einen serverseitigen 302 auf eine fremde Domain
   * (silwymagnet-store.com). UpPromote's cookie-basiertes Tracking geht dabei
   * verloren, deshalb hängen wir den Linker-Wert (`?_upl=`) selbst an die
   * Proxy-URL an. Der Worker liest ihn dort aus und reicht ihn an die
   * tatsächliche Ziel-URL weiter (siehe `worker/worker.js`, `checkoutRedirect`).
   */
  function getCheckoutUrl() {
    const base = CONFIG.apiBaseUrl + CONFIG.endpoints.checkout;
    const token = getStoredToken();
    const linkerValue = getUpPromoteLinkerValue();

    if (!token && !linkerValue) return base;

    const params = new URLSearchParams();
    // Token an die Checkout-URL hängen, damit der Proxy denselben Cart öffnet.
    // (Bei Top-Level-Navigation gibt es keinen Body — daher Query-Param.)
    if (token) params.set('ut', token);
    if (linkerValue) params.set('_upl', linkerValue);
    return `${base}?${params.toString()}`;
  }

  // ─── Remote → Lokal-Abgleich (bei Seitenaufruf) ──────────────────────────────
  // Der Push-Sync oben (Shopify → Remote) deckt nicht den Fall ab, dass ein
  // Produkt AUSSERHALB des Shops aus dem Remote-Warenkorb entfernt wird (z.B.
  // direkt bei Keystone/über einen anderen Kanal). Damit der Shopify-Cart nicht
  // Karteileichen zeigt, wird bei jedem Seitenaufruf einmalig `GetCartSummary`
  // abgerufen und mit dem lokalen Cart abgeglichen.

  /**
   * Wandelt einen Keystone-ProductCode zurück in die Shopify-SKU um
   * (Gegenstück zu {@link toProductCode}). Codes ohne unser Präfix gehören zu
   * einem anderen System/Katalog und werden ignoriert.
   * z.B. "SWYS030-1313-1" → "S030-1313-1".
   */
  function fromProductCode(code) {
    if (typeof code !== 'string' || !code.startsWith(CONFIG.productCodePrefix)) return null;
    return code.slice(CONFIG.productCodePrefix.length);
  }

  /**
   * Führt einen einzelnen `/cart/change.js`-Call aus (gleiches Muster wie das
   * Theme selbst in `LineItemQuantity._changeLineItemQuantity` verwendet) und
   * gibt den aktualisierten Warenkorb zurück.
   */
  async function applyLineChange(shopifyRoot, lineKey, quantity, sectionsToBundle) {
    return fetch(`${shopifyRoot}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity, sections: sectionsToBundle }),
    }).then((r) => r.json());
  }

  /**
   * Gleicht den lokalen Shopify-Warenkorb gegen den Remote-Cart (Keystone) ab —
   * in BEIDE Richtungen, aber NUR für SKUs, die bereits als Line-Item im
   * Shopify-Cart existieren:
   *   - Remote hat WENIGER (Produkt entfernt/Menge reduziert, z.B. über einen
   *     anderen Kanal bei Keystone) → Shopify-Line-Item(s) per `/cart/change.js`
   *     auf die remote gemeldete Menge herunterziehen (bzw. ganz entfernen).
   *   - Remote hat MEHR (Menge dort erhöht) → die Differenz wird auf den
   *     letzten bestehenden Shopify-Line-Item dieser SKU aufgeschlagen.
   *
   * Eine SKU, die remote existiert, aber lokal NOCH GAR NICHT im Cart liegt,
   * wird bewusst NICHT neu angelegt: das würde bedeuten, aus einem reinen
   * ProductCode heraus einen neuen Shopify-Line-Item zu erzeugen (Varianten-
   * Auflösung, Preis, Verfügbarkeit) — eine deutlich riskantere Operation als
   * das Anpassen eines bereits vorhandenen Items. Für bereits vorhandene Items
   * ist eine Mengen-Erhöhung dagegen ein normaler `/cart/change.js`-Call ohne
   * dieses Risiko.
   *
   * Hinweis (siehe auch OFFENE PUNKTE in den Projekt-Notizen): wie beim
   * bestehenden Push-Sync wird angenommen, dass jede SKU im Shopify-Cart ein
   * Keystone-Produkt ist. ProductCodes ohne unser Präfix werden ignoriert;
   * SKUs, die remote nie ankamen (z.B. weil GetCartSummary sie nicht liefert),
   * werden wie „remote entfernt" (Menge 0) behandelt.
   */
  async function reconcileWithRemoteCart() {
    const shopifyRoot = (typeof Shopify !== 'undefined' && Shopify.routes?.root) || '/';

    let cart;
    try {
      cart = await fetch(`${shopifyRoot}cart.js`).then((r) => r.json());
    } catch (err) {
      error('Remote-Cart-Abgleich: aktueller Warenkorb konnte nicht geladen werden.', err);
      return;
    }

    // sku → Liste der Line-Items (key + quantity), in Cart-Reihenfolge.
    const itemsBySku = {};
    for (const item of cart.items || []) {
      if (!item.sku) continue;
      (itemsBySku[item.sku] = itemsBySku[item.sku] || []).push(item);
    }

    // Nichts im lokalen Cart, das synct werden könnte → GetCartSummary (und ggf.
    // eine Token-Generierung beim Proxy) für anonyme Leerkorb-Besuche sparen.
    if (Object.keys(itemsBySku).length === 0) {
      log('Remote-Cart-Abgleich übersprungen: lokaler Warenkorb hat keine SKU-Produkte.');
      return;
    }

    let summary;
    try {
      summary = await getCartSummary('full');
    } catch (err) {
      warn('Remote-Cart-Abgleich: GetCartSummary fehlgeschlagen – überspringe (fail-open).', err);
      return;
    }
    if (!summary || summary.Successful === false) {
      warn('Remote-Cart-Abgleich: GetCartSummary nicht erfolgreich – überspringe.', summary && summary.Error);
      return;
    }

    const remoteState = {}; // sku → Menge
    for (const line of Array.isArray(summary.SummaryDetail) ? summary.SummaryDetail : []) {
      const sku = line && fromProductCode(line.ProductCode);
      if (!sku) continue;
      const qty = typeof line.Quantity === 'number' ? line.Quantity : 0;
      remoteState[sku] = (remoteState[sku] || 0) + qty;
    }

    // Vor jedem cart/change.js-Call einmalig die zu bündelnden Sections
    // einsammeln (wie im Theme selbst), damit Mini-Cart/Drawer nach dem
    // Abgleich mit aktuellem HTML statt nur mit rohen Zahlen aktualisiert wird.
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));

    let changed = false;
    for (const [sku, items] of Object.entries(itemsBySku)) {
      const localQty = items.reduce((sum, i) => sum + i.quantity, 0);
      const remoteQty = remoteState[sku] || 0;
      if (remoteQty === localQty) continue; // stimmt überein → nichts zu tun

      if (remoteQty > localQty) {
        // Remote hat mehr (z.B. Menge dort erhöht) → Differenz auf den letzten
        // bestehenden Line-Item dieser SKU aufschlagen. Kein neuer Line-Item.
        const target = items[items.length - 1];
        const diff = remoteQty - localQty;
        const newQty = target.quantity + diff;
        log(`Remote-Cart-Abgleich: ${sku} lokal=${localQty} remote=${remoteQty} → lokal auf ${newQty} erhöhen`);
        try {
          cart = await applyLineChange(shopifyRoot, target.key, newQty, sectionsToBundle);
          changed = true;
          log(`✓ Lokal erhöht (remote hat mehr): ${sku} ${target.quantity} → ${newQty}`);
        } catch (err) {
          error(`Remote-Cart-Abgleich: cart/change.js (Erhöhung) fehlgeschlagen für ${sku}:`, err);
        }
        continue;
      }

      // remoteQty < localQty: von hinten nach vorne reduzieren, bis toRemove aufgebraucht ist.
      let toRemove = localQty - remoteQty;
      log(`Remote-Cart-Abgleich: ${sku} lokal=${localQty} remote=${remoteQty} → ${toRemove} lokal entfernen`);

      for (let idx = items.length - 1; idx >= 0 && toRemove > 0; idx--) {
        const item = items[idx];
        const reduceBy = Math.min(item.quantity, toRemove);
        const newQty = item.quantity - reduceBy;
        try {
          cart = await applyLineChange(shopifyRoot, item.key, newQty, sectionsToBundle);
          changed = true;
          toRemove -= reduceBy;
          log(`✓ Lokal angeglichen (remote bereits entfernt): ${sku} ${item.quantity} → ${newQty}`);
        } catch (err) {
          error(`Remote-Cart-Abgleich: cart/change.js fehlgeschlagen für ${sku}:`, err);
          break;
        }
      }
    }

    if (!changed) {
      log('Remote-Cart-Abgleich: Shopify-Cart stimmt bereits mit Remote-Cart überein.');
      return;
    }

    // previousCartState VOR dem Dispatch aktualisieren: der eigene
    // cart:change-Listener (onCartChange → syncCartToRemote) berechnet sonst
    // ein Delta gegen den alten Stand und würde die gerade remote veranlasste
    // Entfernung fälschlich noch einmal an removeFromCart melden.
    previousCartState = buildCartState(cart.items);

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'silwy:remote-sync', cart },
    }));
    log('Warenkorb an Remote-Cart angeglichen (Cart-Icon/Mini-Cart aktualisiert).');
  }

  // ─── Sync-Logik ───────────────────────────────────────────────────────────────

  /**
   * Berechnet das Delta zwischen vorherigem und neuem Cart-Zustand und führt
   * die entsprechenden Remote-Operationen aus.
   */
  async function syncCartToRemote(shopifyItems) {
    const newState = buildCartState(shopifyItems);
    const operations = [];

    for (const [sku, newQty] of Object.entries(newState)) {
      const prevQty = previousCartState[sku] || 0;
      const delta = newQty - prevQty;
      if (delta > 0) {
        operations.push({ type: 'add', sku, qty: delta });
      } else if (delta < 0) {
        operations.push({ type: 'remove', sku, qty: Math.abs(delta) });
      }
    }

    for (const [sku, prevQty] of Object.entries(previousCartState)) {
      if (!(sku in newState)) {
        operations.push({ type: 'remove', sku, qty: prevQty });
      }
    }

    if (operations.length === 0) {
      log('Keine Änderungen zu synchronisieren.');
      previousCartState = newState;
      return;
    }

    log(`Synchronisiere ${operations.length} Änderung(en):`, operations);

    for (const op of operations) {
      try {
        let result;
        if (op.type === 'add') {
          result = await addToCart(op.sku, op.qty);
          if (result.Successful) {
            log(`✓ Hinzugefügt: ${op.qty}× ${op.sku}`);
          } else {
            warn(`Remote-Cart: addToCart nicht erfolgreich für ${op.sku}`, result);
          }
        } else {
          result = await removeFromCart(op.sku, op.qty);
          if (result.Successful) {
            log(`✓ Entfernt: ${op.qty}× ${op.sku}`);
          } else {
            warn(`Remote-Cart: removeFromCart nicht erfolgreich für ${op.sku}`, result);
          }
        }
      } catch (err) {
        error(`Fehler bei ${op.type} ${op.sku}:`, err);
      }
    }

    previousCartState = newState;
  }

  // ─── Event-Handler ────────────────────────────────────────────────────────────

  function onCartChange(event) {
    const cart = event.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) {
      warn('cart:change ohne gültige cart.items erhalten.');
      return;
    }

    log(`cart:change (${event.detail?.baseEvent || 'unbekannt'}) – ${cart.item_count} Artikel`);

    const items = cart.items;
    syncQueue = syncQueue
      .then(() => syncCartToRemote(items))
      .catch(err => error('Sync-Fehler:', err));
  }

  // ─── Verfügbarkeits-Gating (Ausverkauft-Anzeige + harte Add-Sperre) ────────────

  /**
   * Zeigt das Ausverkauft-Badge oben links auf einer Grid-Karte an.
   *   - Ist bereits ein von Liquid gerendertes <sold-out-badge> vorhanden
   *     (Shopify selbst meldet die Variante als nicht verfügbar), wird das
   *     einfach eingeblendet statt ein Duplikat zu erzeugen.
   *   - Andernfalls (Keystone meldet ausverkauft, Shopify-Bestand sagt aber
   *     "verfügbar" → Liquid hat gar kein Badge gerendert) wird ein Badge
   *     dynamisch erzeugt und in die bestehende `.product-card__badge-list`
   *     eingehängt (die Liste wird bei Bedarf ebenfalls neu angelegt).
   */
  function showCardBadge(container) {
    const native = container.querySelector('sold-out-badge');
    if (native) {
      native.hidden = false;
      return;
    }

    let list = container.querySelector('.product-card__badge-list');
    if (!list) {
      const figure = container.querySelector('.product-card__figure');
      if (!figure) return;
      list = document.createElement('div');
      list.className = 'product-card__badge-list';
      figure.prepend(list);
    }

    let badge = list.querySelector('[data-silwy-soldout-badge]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'badge badge--sold-out';
      badge.setAttribute('data-silwy-soldout-badge', '1');
      badge.textContent = container.dataset.silwySoldoutLabel || CONFIG.soldOutLabel;
      list.appendChild(badge);
    }
    badge.hidden = false;
  }

  /** Blendet ein per {@link showCardBadge} eingeblendetes Badge wieder aus. */
  function hideCardBadge(container) {
    // Ein von Liquid gerendertes <sold-out-badge> lassen wir unangetastet -
    // dessen Sichtbarkeit ist Shopify's eigener Zustand, nicht Keystone's.
    const injected = container.querySelector('[data-silwy-soldout-badge]');
    if (injected) injected.hidden = true;
  }

  // ─── Keystone-Sichtbarkeits-Gate (PDP-Buy-Buttons, US-Markt) ─────────────────
  // Das Theme rendert den ATC-/Payment-Button-Wrapper im US-Markt mit dem
  // `hidden`-Attribut (siehe snippets/buy-buttons.liquid, [data-silwy-keystone-
  // gate]) - kein Button-Flash, solange der Keystone-Check noch läuft. Sobald
  // ein Ergebnis da ist, wird der Wrapper eingeblendet: verfügbar -> normaler
  // ATC-Button, nicht verfügbar -> ATC-Button bleibt sichtbar, aber als
  // "Sold Out" deaktiviert (Ringo, 2026-09-17: Kurskorrektur ggü. der
  // ursprünglichen "dauerhaft unsichtbar"-Variante); der Payment-Button wird
  // in diesem Fall ausgeblendet, siehe togglePaymentButton().

  function getKeystoneGate(container) {
    return container.querySelector('[data-silwy-keystone-gate]');
  }

  function revealKeystoneGate(container) {
    const gate = getKeystoneGate(container);
    console.log('[SilwyGate] revealKeystoneGate()', gate ? 'Gate gefunden, wird sichtbar gemacht' : 'KEIN Gate-Element im Container gefunden!', container);
    if (gate) gate.hidden = false;
  }

  function hideKeystoneGate(container) {
    const gate = getKeystoneGate(container);
    console.log('[SilwyGate] hideKeystoneGate()', gate ? 'Gate gefunden, wird versteckt' : 'KEIN Gate-Element im Container gefunden!', container);
    if (gate) gate.hidden = true;
  }

  /**
   * Blendet den Payment-/Dynamic-Checkout-Button (Shop Pay etc.) separat vom
   * ATC-Button ein/aus. Der bestehende Liquid-Style-Block versteckt ihn nur
   * anhand von silwy_effective_available, das im US-Markt fest auf true
   * steht (siehe buy-buttons.liquid) - hier übernimmt JS die tatsächliche
   * Keystone-Verfügbarkeit. Anders als der ATC-Button bekommt er bei
   * Nichtverfügbarkeit keinen "Sold Out"-Text, sondern wird ausgeblendet.
   */
  function togglePaymentButton(container, visible) {
    const payBtn = container.querySelector('.shopify-payment-button');
    if (payBtn) payBtn.style.display = visible ? '' : 'none';
  }

  /**
   * Markiert ein Element als ausverkauft.
   *   - Grid-Karte (<product-card>): Add-to-Cart / Quick-Buy ausblenden,
   *     Ausverkauft-Badge oben links einblenden.
   *   - PDP/Formular: Submit-Button-Text auf den Sold-Out-Text umbenennen.
   * (Die harte Add-Sperre verhindert das Hinzufügen in beiden Fällen.)
   */
  function markSoldOut(container) {
    if (!container || container.hasAttribute('data-silwy-soldout')) return;
    container.setAttribute('data-silwy-soldout', '1');

    if (container.matches('product-card')) {
      const quickBuy = container.querySelector('.product-card__quick-buy');
      if (quickBuy) {
        quickBuy.dataset.silwyPrevDisplay = quickBuy.style.display || '';
        quickBuy.style.display = 'none';
      }
      showCardBadge(container);
      return;
    }

    // PDP/Formular: ATC-Button bleibt sichtbar, wird aber deaktiviert und mit
    // Sold-Out-Text beschriftet (wie ein von Liquid mit disabled=true
    // gerenderter Button); Payment-Button wird ausgeblendet.
    revealKeystoneGate(container);
    togglePaymentButton(container, false);

    const label = container.dataset.silwySoldoutLabel || CONFIG.soldOutLabel;
    const btn = container.querySelector('button[type="submit"]');
    if (btn) {
      if (btn.dataset.silwyOrigText == null) btn.dataset.silwyOrigText = btn.textContent;
      btn.textContent = label;
      btn.disabled = true;
      btn.classList.add('button--subdued');
      if (btn.classList.contains('button--secondary')) {
        btn.dataset.silwyHadSecondary = '1';
        btn.classList.remove('button--secondary');
      }
    }
  }

  /** Entfernt den Ausverkauft-Zustand (falls Produkt wieder verfügbar). */
  function clearSoldOut(container) {
    if (!container || !container.hasAttribute('data-silwy-soldout')) return;
    container.removeAttribute('data-silwy-soldout');

    if (container.matches('product-card')) {
      const quickBuy = container.querySelector('.product-card__quick-buy');
      if (quickBuy && quickBuy.dataset.silwyPrevDisplay != null) {
        quickBuy.style.display = quickBuy.dataset.silwyPrevDisplay;
        delete quickBuy.dataset.silwyPrevDisplay;
      }
      hideCardBadge(container);
      return;
    }

    // PDP/Formular: Keystone-Gate wieder einblenden, Button + Payment-Button
    // in den normalen (nicht-disabled) Zustand zurücksetzen.
    revealKeystoneGate(container);
    togglePaymentButton(container, true);

    const btn = container.querySelector('button[type="submit"]');
    if (btn) {
      if (btn.dataset.silwyOrigText != null) {
        btn.textContent = btn.dataset.silwyOrigText;
        delete btn.dataset.silwyOrigText;
      }
      btn.disabled = false;
      btn.classList.remove('button--subdued');
      if (btn.dataset.silwyHadSecondary === '1') {
        btn.classList.add('button--secondary');
        delete btn.dataset.silwyHadSecondary;
      }
    }
  }

  /**
   * Prüft die Verfügbarkeit der SKU eines Containers und setzt/entfernt den
   * Ausverkauft-Zustand entsprechend.
   */
  async function gateContainer(container) {
    const sku = container.dataset.silwySku;
    console.log(`[SilwyGate] gateContainer() gestartet - SKU: "${sku}"`, container);
    if (!sku) {
      console.log('[SilwyGate] gateContainer() abgebrochen - keine SKU am Container, Gate bleibt wie es ist.', container);
      return;
    }
    const availabilityResult = await getAvailability(sku);
    const { available } = availabilityResult;
    console.log(`[SilwyGate] gateContainer() Ergebnis für SKU "${sku}":`, availabilityResult);

    // Gate (PDP-Formular via [data-silwy-keystone-gate] in buy-buttons.liquid,
    // Grid-Karte via .product-card__quick-buy in product-card.liquid - beide
    // nutzen dasselbe Attribut) UNCONDITIONAL freischalten - nicht nur über
    // die guard-geschützten mark/clearSoldOut-Funktionen, weil der
    // Startzustand bereits "hidden" ist und der ERSTE Check ihn freischalten
    // muss (clearSoldOut() würde ohne vorherigen markSoldOut()-Aufruf sonst
    // no-op'en, siehe deren Guard). mark/clearSoldOut laufen zusätzlich für
    // Button-Text/-State-Bookkeeping (PDP: Hard-Lock-Pfad in
    // onProductFormSubmitCapture + Payment-Button; Grid: Quick-Buy
    // ausblenden + Sold-Out-Badge) und sind für Container ohne Gate
    // (revealKeystoneGate/hideKeystoneGate no-op'en dann einfach) unverändert
    // nutzbar.
    revealKeystoneGate(container);
    if (available) clearSoldOut(container);
    else markSoldOut(container);
  }

  // Lazy: nur sichtbare [data-silwy-sku]-Elemente prüfen (Grid-Performance).
  let gateObserver = null;
  function observeGate(el) {
    if (!el || el.dataset.silwyObserved) return;
    el.dataset.silwyObserved = '1';
    console.log(`[SilwyGate] observeGate() registriert - SKU: "${el.dataset.silwySku}", Tag: ${el.tagName.toLowerCase()}`, el);

    // PDP-/Quick-Buy-Formulare (alles außer <product-card>) sind i.d.R.
    // einzeln auf der Seite und sofort sichtbarkeitsrelevant (Keystone-Gate
    // für den ATC-/Payment-Button) - anders als Grid-Karten gibt es hier
    // keinen Lazy-Load-Performance-Vorteil. Sofort/eager prüfen statt über
    // IntersectionObserver, der bei Hintergrund-Tabs bzw. wenn der Browser
    // das Rendering drosselt (Tab nicht fokussiert/sichtbar) stark verzögert
    // oder gar nicht feuert - das würde den Button dauerhaft versteckt lassen,
    // obwohl Keystone längst "verfügbar" gemeldet hat (Ringo, 2026-09-17).
    if (!el.matches('product-card')) {
      gateContainer(el);
      return;
    }

    if (!('IntersectionObserver' in window)) { gateContainer(el); return; }
    if (!gateObserver) {
      gateObserver = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            gateContainer(entry.target);
          }
        }
      }, { rootMargin: '200px' });
    }
    gateObserver.observe(el);
  }

  /** Alle noch nicht beobachteten [data-silwy-sku]-Elemente registrieren. */
  function scanGates(root = document) {
    root.querySelectorAll('[data-silwy-sku]:not([data-silwy-observed])').forEach(observeGate);
  }

  /**
   * Harte Add-Sperre: product-form-Submit in der Capture-Phase abfangen.
   * Ist die SKU des Formulars nachweislich nicht verfügbar → Submit stoppen,
   * damit das Produkt NICHT in den Shopify-Cart gelangt.
   */
  function resolveFormSku(form) {
    if (form.dataset && form.dataset.silwySku) return form.dataset.silwySku;
    const host = form.closest && form.closest('[data-silwy-sku]');
    return host ? host.dataset.silwySku : null;
  }

  function isProductForm(el) {
    return el && el.matches && el.matches(
      'form[is="product-form"], form[action*="/cart/add"], form.shopify-product-form',
    );
  }

  async function onProductFormSubmitCapture(event) {
    const form = event.target;
    if (!isProductForm(form)) return;

    // Bypass-Flag nach freigegebenem Re-Submit.
    if (form.dataset.silwyBypass) {
      delete form.dataset.silwyBypass;
      return;
    }

    const sku = resolveFormSku(form);
    if (!sku) return; // keine SKU bekannt → nicht blockieren

    const cached = availabilityCache.get(sku);
    if (cached && Date.now() - cached.ts < CONFIG.availabilityTtlMs) {
      if (!cached.available) blockSubmit(event, form, sku);
      return; // verfügbar → durchlassen
    }

    // Unbekannt: Submit stoppen, async prüfen, bei Verfügbarkeit erneut absenden.
    event.preventDefault();
    event.stopImmediatePropagation();
    const { available } = await getAvailability(sku);
    if (available) {
      form.dataset.silwyBypass = '1';
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    } else {
      markSoldOut(form.closest('[data-silwy-sku]') || form);
      notifySoldOut(sku);
    }
  }

  function blockSubmit(event, form, sku) {
    event.preventDefault();
    event.stopImmediatePropagation();
    markSoldOut(form.closest('[data-silwy-sku]') || form);
    notifySoldOut(sku);
  }

  function notifySoldOut(sku) {
    warn(`Add blockiert – ${sku} bei Keystone nicht verfügbar.`);
    document.dispatchEvent(new CustomEvent('silwy:sold-out', { detail: { sku } }));
  }

  // ─── Initialisierung ──────────────────────────────────────────────────────────

  /**
   * Initialisiert die Bibliothek:
   *   Aktuellen Shopify-Warenkorb als Baseline laden — fertig.
   *   Kein Token-Roundtrip, kein localStorage.
   */
  async function init() {
    try {
      const shopifyRoot = (typeof Shopify !== 'undefined' && Shopify.routes?.root) || '/';
      const cart = await fetch(`${shopifyRoot}cart.js`).then(r => r.json());
      previousCartState = buildCartState(cart.items);

      initialized = true;
      log('Initialisiert. Aktueller Warenkorb-Zustand:', previousCartState);
    } catch (err) {
      error('Initialisierungsfehler – Remote-Cart-Sync nicht aktiv:', err);
    }
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────────

  // cart:change sofort registrieren – Events werden in syncQueue eingereiht
  // und warten automatisch auf den Abschluss von init().
  document.addEventListener('cart:change', onCartChange);

  // Checkout-Button ([data-remote-checkout]): beim Klick die dynamische
  // Checkout-URL mit dem AKTUELLEN Token (aus localStorage) nutzen, statt des
  // statischen href. So öffnet der Checkout denselben Cart, in den synct wurde.
  // Delegiert, damit es auch für nachgerenderte Cart-Drawer funktioniert.
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('[data-remote-checkout]');
    if (!link) return;
    event.preventDefault();
    window.location.href = getCheckoutUrl();
  });

  // Harte Add-Sperre: Submit in der Capture-Phase abfangen (vor dem product-form-
  // Handler des Themes), damit nicht-verfügbare Produkte nicht in den Cart kommen.
  document.addEventListener('submit', onProductFormSubmitCapture, true);

  // PDP/Quick-Buy: Bei Variantenwechsel die SKU am Formular aktualisieren und neu
  // prüfen. Das Theme feuert `variant:change` auf dem Formular mit detail.variant.
  document.addEventListener('variant:change', (event) => {
    const form = event.target;
    const variant = event.detail && event.detail.variant;
    if (!form || !variant || typeof variant.sku !== 'string') return;
    form.dataset.silwySku = variant.sku;
    const container = form.closest('[data-silwy-sku]') || form;
    if (container !== form) container.dataset.silwySku = variant.sku;
    // Bei Variantenwechsel erst mal wieder verstecken (neue SKU = neuer, noch
    // nicht bestätigter Zustand) statt optimistisch anzuzeigen - gateContainer()
    // blendet nur nach bestätigter Keystone-Verfügbarkeit wieder ein.
    hideKeystoneGate(container);
    gateContainer(container);
  });

  // Verfügbarkeits-Gating: sichtbare Karten/PDP-Bereiche prüfen — auch solche,
  // die nachträglich ins DOM kommen (Ajax-Collection, Quick-Buy-Drawer).
  scanGates();
  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-silwy-sku]')) observeGate(node);
          if (node.querySelectorAll) scanGates(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  const initPromise = new Promise((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve());
    } else {
      resolve();
    }
  });
  syncQueue = initPromise
    .then(init)
    .then(reconcileWithRemoteCart)
    .catch(err => error('Init-Fehler:', err));

  // ─── Öffentliche API ──────────────────────────────────────────────────────────

  /**
   * `window.SilwyRemoteCart` – öffentliche API-Schnittstelle
   *
   * @example
   * SilwyRemoteCart.getCartSummary().then(console.log);
   * window.location.href = SilwyRemoteCart.getCheckoutUrl();
   * SilwyRemoteCart.setDebug(true);
   */
  window.SilwyRemoteCart = {
    addToCart,
    removeFromCart,
    getCartSummary,
    checkProductAvailability,
    getCheckoutUrl,

    /** Ja/Nein-Verfügbarkeit einer SKU bei Keystone (gecacht). */
    isAvailable,

    /** Rohe Verfügbarkeit { available, quantity } einer SKU (gecacht). */
    getAvailability,

    /** [data-silwy-sku]-Elemente (neu) scannen und gaten. */
    refreshGates: (root) => scanGates(root || document),

    /** Gibt eine Kopie der aktuellen Konfiguration zurück */
    getConfig: () => ({ ...CONFIG }),

    /** Aktiviert/deaktiviert das Debug-Logging */
    setDebug: (enabled) => {
      CONFIG.debug = Boolean(enabled);
      log(`Debug-Modus ${CONFIG.debug ? 'aktiviert' : 'deaktiviert'}.`);
    },

    /** Gibt den aktuell getrackten Warenkorb-Zustand zurück (SKU → Menge) */
    getTrackedState: () => ({ ...previousCartState }),

    /** Gibt an, ob die Bibliothek erfolgreich initialisiert wurde */
    isInitialized: () => initialized,

    /**
     * Manueller Re-Abgleich Remote → Lokal (normalerweise läuft das einmalig
     * beim Seitenaufruf automatisch). Nützlich zum Debuggen oder um z.B. nach
     * längerer Inaktivität einer Seite gezielt neu abzugleichen.
     */
    syncFromRemote: reconcileWithRemoteCart,
  };

})();
